<template>
  <CoreDialog
    v-model="isDialogActive"
    :title="`${i18next.t('Выберите свой город в', { ns: userLanguage })} ${namePred}`"
    data-qa="dialog-town-select"
    size="xl"
  >
    <template #prependBtn>
      <v-icon
        class="cursor-pointer"
        data-qa="dialog-town-select-close"
        @click="isDialogActive = false"
      >
        ui-icon-close-not-a-circle
      </v-icon>
    </template>

    <template #default>
      <SearchCombobox
        use-no-data-slot
        :initial-data="searchData"
        :placeholder="i18next.t('Начните вводить название, а мы подскажем', { ns: userLanguage })"
        class="pb-2"
        data-qa="dialog-town-select-search"
        @search:default="handleSearchDefault"
        @search:text="handleSearchText"
        @search:class="handleSearchClass"
      >
        <template #no-data>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title class="text-secondary--text pa-2">
                {{ helpText }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </SearchCombobox>

      <div class="py-3 mt-2">
        <TownList
          :towns="mainTowns"
          data-qa="dialog-main-town-select-list"
          text-style="text-subtitle-1"
          @click:redirect="redirectTown"
        />
      </div>
      <TownList
        :towns="towns"
        data-qa="dialog-town-select-list"
        text-style="text-body-1"
        @click:redirect="redirectTown"
      />
      <div class="mt-6">
        <div class="text-h6 pa-3">
          {{ otherCountriesTitle }}
        </div>
        <TownList
          :towns="otherCountries"
          text-style="text-body-1"
          data-qa="dialog-country-select-list"
          class="mt-4"
          @click:redirect="redirectCountry"
        />
      </div>
    </template>
  </CoreDialog>
</template>

<script setup lang="ts">
import {
  onBeforeMount,
  Ref,
  ref,
  computed,
} from 'vue'
import i18next from 'i18next'
import getUserLanguage from 'src/utils/getUserLanguage'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import SearchCombobox from 'www/vue_components/common/core/SearchCombobox/SearchCombobox.vue'
import TownList from 'www/vue_components/desktop/ChooseTownDialog/components/TownList.vue'
import { Country, Town } from 'www/vue_components/desktop/ChooseTownDialog/types'
import { getTownList, getTownsByQuery } from 'www/vue_components/desktop/ChooseTownDialog/api'

const isDialogActive = ref(false)
const towns: Ref<Town[]> = ref([])
const otherCountries: Ref<Town[]> = ref([])
const mainTowns: Ref<Town[]> = ref([])

const { namePred } = window.COUNTRY_DATA
const userLanguage = getUserLanguage()
const otherCountriesTitle = i18next.t('Другие страны', { ns: userLanguage })
const minQueryLength = 3

onBeforeMount(async () => {
  window.addEventListener('choose-town-dialog:open', async () => {
    if (!towns.value.length && !mainTowns.value.length) {
      const response = await getTownList()

      response.data.towns.forEach((town: Town) => {
        if (town.is_main_town) {
          mainTowns.value.push(town)

          return
        }

        towns.value.push(town)
      })

      otherCountries.value = response.data.other_countries
    }

    isDialogActive.value = true
  })
})

const searchData: Ref<string[]> = ref([])
const searchResult: Ref<Town[]> = ref([])
const searchText = ref('')
const helpText = computed(() => {
  if (!searchData.value.length && searchText.value.length > minQueryLength) {
    return i18next.t('Совпадений не найдено', { ns: userLanguage })
  }

  return `${i18next.t('Введите', { ns: userLanguage })} ${minQueryLength} ${i18next.t('символа для поиска', { ns: userLanguage })}`
})

function handleSearchDefault() {
  searchText.value = ''
  searchData.value = []
}

function handleSearchClass(townName: string) {
  const selectedTown = searchResult.value.find((town: Town) => townName === town.name)

  window.location.href = selectedTown ? `/${selectedTown.translit}` : '/'
}

async function handleSearchText(text: string) {
  searchText.value = text

  if (text.length < minQueryLength) {
    return
  }

  try {
    const response = await getTownsByQuery(text)

    searchResult.value = response.data
    searchData.value = searchResult.value.map((town: Town) => town.name) || []
  } catch {
    searchData.value = []
  }
}

function redirectTown(town: Town) {
  const { iso } = window.LANGUAGE_DATA.currentLanguage
  const { defaultLanguageIso } = window.LANGUAGE_DATA

  if (iso === defaultLanguageIso) {
    window.location.href = `/${town.translit}/`

    return
  }

  window.location.href = `/${iso}/${town.translit}/`
}

function redirectCountry(country: Country) {
  window.location.href = country.main_page_url
}
</script>
